// frontend/src/components/common/ResultCount.jsx
import React from 'react';
import { Typography } from '@mui/material';
import { formatInt } from '../../utils/format';

/**
 * ResultCount — the ONE result counter of a list.
 *
 * @description
 *   Renders "N <noun>", "N of M <noun>" or "N+ <noun>" as a persistent
 *   `role="status"` live region, so a search or filter change is announced.
 *   It is deliberately the ONLY counter of a list: the footer carries the page
 *   range ("1-50 of 50"), never the stock, and a second "Products: 235" line in
 *   the toolbar or the footer is what this component exists to delete.
 *
 *   M is the stock WITHOUT defaults. That is the whole point of the second
 *   number: it answers the one question a counter has to answer — "am I seeing
 *   everything?" — and a default that silently hides rows becomes visible
 *   ("42 of 312 cases" while 270 closed cases are hidden by default).
 *
 *   🔴 The count never falls back to 0. When the server caps the list and the
 *   total is missing, the text is "50+ <noun>" and the total is NOT guessed
 *   (Regel 22 §Component rules 3, "Unknown is never 0"). A FAILED fetch is the
 *   CALLER's call: it renders `EmptyState` and passes no `n`, so this component
 *   draws an empty region — it never invents a confident zero.
 *
 *   🔴 At zero HITS the counter still renders ("0 of 312 cases"). Staying silent
 *   there is the CALLER's call, not a property of this component — the component
 *   does not decide that for it.
 *
 *   🔴 **And the two callers decide DIFFERENTLY, which is why this is not a
 *   rule** (R16 W4, 2026-10-14 — this paragraph claimed the opposite until
 *   then: "the caller leaves the counter out and lets the empty state speak").
 *   `/rma` KEEPS it: N4.5/P28 (`tabellen-filter-suche.md:1493`) compares the
 *   ROLE SET of the children of `liste-z1` between `filter` and `suche-leer`,
 *   and the controls of the bar stay standing in the empty state — "auch der
 *   Treffer-Zähler" (`RMAOverviewLayout.jsx:851-856`). Dropping the counter
 *   there would change that set and turn P28 red. So: the counter is a
 *   building block with a switch, and the surface that wants it gone must pass
 *   no `n` — do not read a general rule out of either surface.
 *
 *   🔴 The region exists from the first paint. On a reload the old text stays
 *   and only `aria-busy` is set; the number does not jump to 0 and back. A live
 *   region that appears together with its message is announced unreliably, so
 *   an unknown count renders an EMPTY region rather than none.
 *
 *   🔴 `aria-busy` sits on THIS component's own region (Plan §G0.2, the
 *   "N of M <noun>" line). The VIEW additionally sets it on the data panel
 *   (Konzept §8.2) — two places with two scopes: the counter announces its own
 *   update, the panel dims the rows. G1-G15 must not build a third one.
 *
 *   Numbers go through `utils/format.js` (`formatInt`, locale de-DE fixed,
 *   owner decision 2026-10-06) like every other display value in the app —
 *   `13000` renders as `13.000`, and it is the same figure the footer shows.
 *
 * @useWhen
 *   - The one counter of a list or table, right-aligned in the toolbar (Z1)
 *   - A list that has to answer "of how many?" while a filter or default narrows it
 *   - A capped server list whose total is unknown ("50+ cases")
 *
 * @dontUseFor
 *   - Tab counters — those belong to `UnderlineTabs` (a different number with its own rule)
 *   - Page ranges — the footer shows "1-50 of 50", not the stock
 *   - A failed fetch — render `EmptyState` instead; a 0 here reads as "nothing there"
 *   - A list with ZERO hits — the CALLER decides: `/rma` KEEPS the counter
 *     ("0 cases", N4.5/P28, `RMAOverviewLayout.jsx:851-856`), a surface that
 *     wants it gone passes no `n`. This counter does NOT silence itself at
 *     `n = 0` (R16 W4, 2026-10-14; the earlier wording named only the
 *     drop-it half)
 *   - KPI tiles — a metric is not a list counter
 *
 * @related
 *   - `EmptyState` — the counterpart for a list with nothing to show
 *   - `UnderlineTabs` — carries per-tab counts, a different number
 *
 * @reuseHints
 *   - Every list migration (G1-G15) replaces its hand-rolled "Showing a of b"
 *   - The invoice list and the RMA case list are the first two candidates
 *
 * @props
 *   noun         — {string} Pflicht. Plural noun ("cases"). Throws without it.
 *                  🔴 Bei genau EINEM Treffer steht in der Kurzform die EINZAHL
 *                  („1 message", Hausform `FilterSheet.jsx:296`); die „of"-Form
 *                  bleibt mehrzahlig („1 of 312 messages"). Erwartet wird ein
 *                  regelmaessiger Plural auf „s".
 *   n            — {number|null} the hits. `null` renders an EMPTY region (still one `role="status"`).
 *                  🔴 A NON-numeric `n` (e.g. the string "42") is reported in
 *                  development mode: the counter would silently stay empty while
 *                  the number exists — the same defect as "unknown is never 0",
 *                  only in the other direction (R16-W2). `null`/`undefined` are
 *                  the documented "unknown" case and are NOT reported.
 *   m            — {number|null} the stock without defaults; "of m" appears only when `m !== n`.
 *                  Non-numeric `m` is reported the same way (same defect, one line down).
 *   unknownTotal — {boolean} the server capped the list: renders "n+ <noun>", never a guessed total.
 *   loading      — {boolean} sets `aria-busy`; the text stays (no jump to 0).
 *   unknown      — {boolean} OPT-IN, Vorgabe `false`: die Zahl ist NICHT BEKANNT
 *                  (ein Nachladen ist gescheitert, waehrend die Liste leer
 *                  steht) — rendert „—" statt einer alten oder geratenen Zahl.
 *                  🔴 R15-W1 (G14a-2, Nachbesserung 2, 2026-11-01): ohne diese
 *                  Form stand dort die Zahl des LETZTEN Erfolgslaufs
 *                  („0 of 120 failures") — eine 0 fuer „unbekannt", genau was
 *                  Regel 22 §3 verbietet.
 */
export default function ResultCount({
  noun,
  n = null,
  m = null,
  unknownTotal = false,
  loading = false,
  unknown = false,
}) {
  // Pflicht-Prop, dieselbe Bauform wie `EmptyState.reason` und
  // `RefreshButton.ariaLabel`: ein fehlender Pflicht-Prop ist ein Defekt an der
  // Aufrufstelle, kein Zustand, den die Komponente rendern kann. "42 of 312"
  // ohne Substantiv ist keine Aussage.
  if (!noun || typeof noun !== 'string') {
    throw new Error(
      'ResultCount requires a `noun` (the plural of what is counted, e.g. "cases"). ' +
      'Without it the number states nothing.'
    );
  }

  // 🔴 R16-W2 (Nachbesserung 1, 2026-10-11): ein Tippfehler in `n` machte aus
  // einer VORHANDENEN Zahl still eine leere Region — dieselbe Bauform wie
  // "unbekannt ist nie 0", nur in die andere Richtung: eine Zahl, die es gibt,
  // wird nicht gezeigt. `noun` wirft in diesem Fall; hier wird nur GEMELDET
  // (melden statt werfen, Hausform `EmptyState`), denn eine fehlende Zahl ist
  // ein Defekt an der Aufrufstelle, kein Absturzgrund. `null`/`undefined` sind
  // die DOKUMENTIERTE "unbekannt"-Lage und melden nicht.
  if (process.env.NODE_ENV !== 'production') {
    const schief = [];
    if (n != null && !Number.isFinite(n)) schief.push('n');
    if (m != null && !Number.isFinite(m)) schief.push('m');
    if (schief.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'ResultCount: `' + schief.join('`/`') + '` is not a number — the counter ' +
        'renders empty. Pass a number, or `null` to say "unknown" on purpose.'
      );
    }
  }

  const treffer = Number.isFinite(n) ? n : null;
  const bestand = Number.isFinite(m) ? m : null;

  // 🔴 G4a-Nachbesserung 1 (Fotobefund 2): „1 messages" — die EINZAHL bei eins.
  // Hausform ist die Endung (`FilterSheet.jsx:296`: `result${n === 1 ? '' : 's'}`),
  // und sie gilt nur in der KURZFORM („N <noun>"): in der „of"-Form richtet sich
  // das Substantiv nach dem BESTAND („1 of 312 messages" ist richtiges Englisch),
  // und `m === n` laesst die „of"-Form ohnehin weg.
  const einzahl = treffer === 1 && noun.endsWith('s') ? noun.slice(0, -1) : noun;

  let text = '';
  if (unknown) {
    // 🔴 R15-W1: „unbekannt" ist keine Zahl. Der Strich ist die Hausform des
    // Platzhalters (dieselbe wie `CronFailures.STRICH`); der Name fuer
    // Hilfsmittel steht am `aria-label` unten, damit die Region nicht „Strich"
    // vorliest.
    text = '—';
  } else if (treffer !== null) {
    if (unknownTotal) {
      // Der Server kappt: die Gesamtzahl ist UNBEKANNT. "50+ cases", nie eine
      // geratene Zahl (Konzept §4.2, Regel 22 §3).
      text = `${formatInt(treffer)}+ ${noun}`;
    } else if (bestand !== null && bestand !== treffer) {
      // Etwas engt ein — sonst waere m === n und "312 of 312" eine Nullaussage.
      text = `${formatInt(treffer)} of ${formatInt(bestand)} ${noun}`;
    } else {
      text = `${formatInt(treffer)} ${einzahl}`;
    }
  }

  return (
    // Genau EIN `role="status"` je Liste (P8). Die Region steht ab dem ersten
    // Aufbau da — auch leer —, damit ihre spaetere Aenderung angesagt wird.
    <Typography
      component="span"
      role="status"
      aria-busy={loading || undefined}
      aria-label={unknown ? 'Count unknown' : undefined}
      variant="body2"
      color="text.secondary"
      sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
    >
      {text}
    </Typography>
  );
}
